import { createContext, useCallback, useContext, useEffect, useState } from 'react'

const PwaInstallContext = createContext(null)

function getPlatformState() {
  const userAgent = window.navigator.userAgent
  // iPhone/iPad con cualquier navegador (Safari, Chrome, Edge, Firefox…): en iOS
  // no existe beforeinstallprompt y la instalación siempre es manual desde
  // "Compartir". El iPad con iPadOS se presenta como Mac con pantalla táctil.
  const isIos = /iPad|iPhone|iPod/.test(userAgent)
    || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1)
  const isAndroid = /Android/.test(userAgent)
  const isChromium = /Chrome|Chromium|CriOS|Edg|EdgiOS|EdgA/.test(userAgent)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true

  return { isAndroid, isChromium, isIos, isStandalone }
}

function usePwaInstallState() {
  const [platform] = useState(getPlatformState)
  const { isIos, isAndroid, isChromium } = platform
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [isInstalled, setIsInstalled] = useState(platform.isStandalone)

  useEffect(() => {
    const displayMode = window.matchMedia('(display-mode: standalone)')

    function handleBeforeInstallPrompt(event) {
      event.preventDefault()
      setDeferredPrompt(event)
    }

    function handleInstalled() {
      setDeferredPrompt(null)
      setIsInstalled(true)
    }

    function handleDisplayModeChange(event) {
      if (event.matches) setIsInstalled(true)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleInstalled)
    displayMode.addEventListener?.('change', handleDisplayModeChange)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleInstalled)
      displayMode.removeEventListener?.('change', handleDisplayModeChange)
    }
  }, [])

  const install = useCallback(async () => {
    if (!deferredPrompt) return { outcome: 'unavailable' }
    await deferredPrompt.prompt()
    const choice = await deferredPrompt.userChoice
    setDeferredPrompt(null)
    if (choice.outcome === 'accepted') setIsInstalled(true)
    return choice
  }, [deferredPrompt])

  const canNativeInstall = !isInstalled && !isIos && Boolean(deferredPrompt)

  return {
    canNativeInstall,
    install,
    isAndroid,
    isChromium,
    isInstalled,
    isIos,
    isStandalone: isInstalled,
    // iOS: instrucciones manuales; resto: solo si el navegador ofreció beforeinstallprompt.
    shouldShow: !isInstalled && (isIos || canNativeInstall),
  }
}

export function PwaInstallProvider({ children }) {
  const installState = usePwaInstallState()
  return <PwaInstallContext.Provider value={installState}>{children}</PwaInstallContext.Provider>
}

export function usePwaInstall() {
  const context = useContext(PwaInstallContext)
  if (!context) throw new Error('usePwaInstall debe utilizarse dentro de PwaInstallProvider.')
  return context
}